import { useEffect, useState, useCallback } from "react";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";

export function useFetch(fn, deps = []) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const reload = useCallback(async () => {
    setLoading(true);
    try {
      setData(await fn());
    } catch (e) {
      setData(null);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  useEffect(() => {
    reload();
  }, [reload]);
  return { data, loading, reload, setData };
}

let CURRENCY = "USD";
export function setCurrency(c) { CURRENCY = c === "HTG" ? "HTG" : "USD"; }
export function getCurrency() { return CURRENCY; }
let PRESENTER_MODE = false;
export function setPresenterMode(on) { PRESENTER_MODE = !!on; }
export const money = (n) => {
  if (PRESENTER_MODE) return CURRENCY === "HTG" ? "XXX HTG" : "$XXX";
  const v = Number(n || 0).toFixed(2);
  return CURRENCY === "HTG" ? `${v} HTG` : `$${v}`;
};

export const fmtDate = (s) => {
  if (!s) return "—";
  try {
    return new Date(s).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return s;
  }
};

export const fmtDMY = (s) => {
  if (!s) return "";
  try {
    const d = new Date(s);
    if (isNaN(d.getTime())) return "";
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    return `${dd}/${mm}/${d.getFullYear()}`;
  } catch {
    return "";
  }
};

const STATUS_STYLES = {
  pending: "bg-accent/20 text-accent-foreground border-accent/40",
  accepted: "bg-emerald-100 text-emerald-800 border-emerald-200",
  paid: "bg-emerald-100 text-emerald-800 border-emerald-200",
  confirmed: "bg-emerald-100 text-emerald-800 border-emerald-200",
  rejected: "bg-red-100 text-red-800 border-red-200",
  unpaid: "bg-red-100 text-red-800 border-red-200",
  present: "bg-emerald-100 text-emerald-800 border-emerald-200",
  absent: "bg-red-100 text-red-800 border-red-200",
  late: "bg-amber-100 text-amber-800 border-amber-200",
  published: "bg-emerald-100 text-emerald-800 border-emerald-200",
};

const STATUS_FR = { pending: "En attente", accepted: "Accepté", paid: "Payé", confirmed: "Confirmé", rejected: "Rejeté", unpaid: "Non payé", present: "Présent", absent: "Absent", late: "Retard", published: "Publié" };

export function StatusBadge({ status, label }) {
  const cls = STATUS_STYLES[status] || "bg-muted text-muted-foreground border-border";
  let lang = "fr";
  try { lang = localStorage.getItem("icdh_lang") || "fr"; } catch (e) { if (process.env.NODE_ENV !== "production") console.error(e); }
  const text = label || (lang === "en" ? status : (STATUS_FR[status] || status));
  return (
    <Badge variant="outline" className={`capitalize font-medium ${cls}`} data-testid={`status-${status}`}>
      {text}
    </Badge>
  );
}

export function Modal({ open, onClose, title, children, footer, wide, xwide }) {
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className={`${xwide ? "max-w-5xl" : wide ? "max-w-2xl" : ""} max-h-[90vh] overflow-y-auto`}>
        <DialogHeader>
          <DialogTitle className="font-heading">{title}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">{children}</div>
        {footer && <DialogFooter>{footer}</DialogFooter>}
      </DialogContent>
    </Dialog>
  );
}

export function Field({ label, children }) {
  return (
    <div className="space-y-2 mb-1">
      <Label className="text-sm font-medium">{label}</Label>
      {children}
    </div>
  );
}

export function TableWrap({ children }) {
  return (
    <div className="border border-border rounded-lg overflow-hidden bg-card overflow-x-auto">
      {children}
    </div>
  );
}
